import styled, { css } from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../../formInputsV2/styles/tokens';

// Same card language as the selected quote card (SelectedQuoteCard.styles):
// white card, hairline border, 16px radius, logo box on the left, price on the
// right. Design only — ServiceComponent keeps its own logic.

export const ServiceCard = styled.div`
  width: 100%;
  margin-top: 10px;
  padding: 16px 20px;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.disabledBorder};
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.05);
  font-family: ${v2FontFamily};

  &.selected {
    border-color: ${v2Colors.primary};
    box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px 16px;
  }
`;

export const ServiceHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 20px;
`;

export const ServiceLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 97px;
  height: 57px;
  padding: 8px;
  border: 1px solid ${v2Colors.disabledSurface};
  border-radius: 12px;
  background: ${v2Colors.inputBackground};

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    width: 88px;
    height: 52px;
    padding: 6px;
  }
`;

export const ServiceTitleBlock = styled.div`
  flex: 1 1 200px;
  min-width: 0;

  h3 {
    margin: 0 0 2px;
    color: ${v2Colors.text};
    font-family: ${v2FontFamily};
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
  }

  p {
    margin: 0;
    color: ${v2Colors.textMuted};
    font-family: ${v2FontFamily};
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1.3;
  }
`;

export const ServiceInfoLink = styled.button`
  display: block;
  margin: 6px 0 0;
  padding: 0;
  border: none;
  background: none;
  color: ${v2Colors.primary};
  font-family: ${v2FontFamily};
  font-size: 12.5px;
  font-weight: 600;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${v2Colors.primaryHover};
  }
`;

export const ServiceBadge = styled.span`
  display: inline-block;
  margin-bottom: 4px;
  padding: 2px 10px;
  border: 1px solid ${v2Colors.dangerBorder};
  border-radius: 999px;
  background: ${v2Colors.dangerSoft};
  color: ${v2Colors.danger};
  font-family: ${v2FontFamily};
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.4;
`;

export const ServicePrice = styled.div`
  text-align: right;

  span {
    display: block;
    margin-bottom: 2px;
    color: ${v2Colors.textMuted};
    font-family: ${v2FontFamily};
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1.3;
  }

  h2 {
    margin: 0;
    color: ${v2Colors.text};
    font-family: ${v2FontFamily};
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    text-align: left;
  }
`;

export const ServiceDivider = styled.div`
  margin: 12px 0;
  border-top: 1px solid ${v2Colors.disabledSurface};
`;

export const ServiceActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  padding: 12px 28px;
  border-radius: 12px;
  font-family: ${v2FontFamily};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
`;

export const ServiceButton = styled.button`
  ${buttonBase}
  border: 1px solid ${v2Colors.primaryBorder};
  background: ${v2Colors.primarySoft};
  color: ${v2Colors.primary};

  &:hover,
  &:focus-visible {
    background: ${v2Colors.primarySoftHover};
  }
`;

export const ServicePrimaryButton = styled.button`
  ${buttonBase}
  border: 1px solid ${v2Colors.primary};
  background: ${v2Colors.primary};
  color: #ffffff;

  &:hover,
  &:focus-visible {
    background: ${v2Colors.primaryHover};
  }
`;

export const ServiceDangerButton = styled.button`
  ${buttonBase}
  border: 1px solid ${v2Colors.dangerBorder};
  background: ${v2Colors.dangerSoft};
  color: ${v2Colors.danger};

  &:hover,
  &:focus-visible {
    background: ${v2Colors.dangerSoftHover};
  }
`;

export const ServiceField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 260px;

  label {
    color: ${v2Colors.textMuted};
    font-family: ${v2FontFamily};
    font-size: 12.5px;
    font-weight: 500;
  }

  input {
    padding: 10px 12px;
    border: 1px solid ${v2Colors.border};
    border-radius: 10px;
    background: ${v2Colors.inputBackground};
    color: ${v2Colors.text};
    font-family: ${v2FontFamily};
    font-size: 15px;
    font-weight: 600;

    &:focus {
      outline: none;
      border-color: ${v2Colors.primary};
      box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
    }
  }
`;

export const ServiceSliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 28px;

  > span {
    flex-shrink: 0;
    color: ${v2Colors.textMuted};
    font-family: ${v2FontFamily};
    font-size: 13px;
    font-weight: 600;
  }

  .slider {
    flex: 1;
    min-width: 0;
  }
`;
